import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/api';
import { 
  Bell, 
  AlertTriangle, 
  Package, 
  Clock, 
  Filter,
  RefreshCw,
  Calendar,
  MessageSquare,
  Slack
} from 'lucide-react';
import './Alerts.css';
import GradientHeading from './GradientHeading';
import Loader from './Loader';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, inventory, samples
  const [days, setDays] = useState(7);
  const [limit, setLimit] = useState(50);
  const [slackMessage, setSlackMessage] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isSlackConnecting, setIsSlackConnecting] = useState(false);
  

  const fetchAlerts = useCallback(async () => {
  setLoading(true);
  setError(null);

  try {
    // Call the unified backend endpoint
    const response = await apiClient.get('/alerts', {
      params: {
        alert_type: filter === 'all' ? 'all' : (filter === 'inventory' ? 'low_inventory' : 'sample_request'),
        days,
        limit
      }
    });

    if (response.data.status === 'success') {
      const alerts = response.data.data.alerts || [];
      alerts.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
      setAlerts(alerts);
    } else {
      setAlerts([]);
      setError(response.data.message || 'Failed to fetch alerts');
    }
  } catch (err) {
    setError(`Failed to fetch alerts: ${err.message}`);
    console.error('Error fetching alerts:', err);
  } finally {
    setLoading(false);
  }
}, [filter, days, limit]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Set the browser tab title
  useEffect(() => {

  }, []);

  // Connect Slack (OAuth)
  const handleConnectSlack = async () => {
    setIsAuthenticating(true);
    setSlackMessage(null);
    try {
      const response = await apiClient.get('/slack/slack-oauth-url', {withCredentials: true, credentials: 'include'});
      if (response.data && response.data.oauth_url) {
        // Center the popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
          response.data.oauth_url, 
          'SlackConnection', 
          `width=${width},height=${height},left=${left},top=${top},status=no,location=no,toolbar=no,menubar=no`
        );
        
        if (popup) {
          setIsSlackConnecting(true);
          const timer = setInterval(() => {
            if (popup.closed) {
              clearInterval(timer);
              setIsSlackConnecting(false);
            }
          }, 1000);
          setSlackMessage({ type: 'info', text: 'Please complete the authorization in the opened window.' });
        }
      } else {
        throw new Error('Failed to get Slack OAuth URL');
      }
    } catch (err) {
      setSlackMessage({ type: 'error', text: err.message || 'Failed to initiate Slack authentication' });
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Test Slack Connection
  const handleTestSlack = async () => {
    setIsAuthenticating(true);
    setSlackMessage(null);
    try {
      const response = await apiClient.post('/slack/test-connection', { credentials: 'include', withCredentials: true });
      if (response.data && response.data.status === 'success') {
        setSlackMessage({ type: 'success', text: 'Test alert sent successfully!' });
      } else {
        throw new Error(response.data.message || 'Failed to send test alert');
      }
    } catch (err) {
      setSlackMessage({ type: 'error', text: err.message || 'Failed to send test alert' });
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Disconnect Slack
  const handleDisconnectSlack = async () => {
    setIsAuthenticating(true);
    setSlackMessage(null);
    try {
      const response = await apiClient.post('/slack/disconnect', { credentials: 'include', withCredentials: true });
      if (response.data && response.data.status === 'success') {
        setSlackMessage({ type: 'success', text: 'Slack integration disconnected.' });
      } else {
        throw new Error(response.data.message || 'Failed to disconnect Slack');
      }
    } catch (err) {
      setSlackMessage({ type: 'error', text: err.message || 'Failed to disconnect Slack' });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case 'low_inventory':
        return <Package className="alert-icon inventory" />;
      case 'new_sample_request':
        return <MessageSquare className="alert-icon sample" />;
      case 'stable_unapproved_sample':
        return <Clock className="alert-icon pending" />;
      default:
        return <AlertTriangle className="alert-icon default" />;
    }
  };

  const getAlertTypeLabel = (alertType) => {
    switch (alertType) {
      case 'low_inventory':
        return 'Low Inventory';
      case 'new_sample_request':
        return 'New Sample Request';
      case 'stable_unapproved_sample':
        return 'Pending Sample';
      default:
        return alertType;
    }
  };

  const getAlertPriority = (alert) => {
    if (alert.alert_type === 'low_inventory') {
      return alert.current_stock <= 5 ? 'high' : 'medium';
    }
    
    if (alert.alert_type === 'stable_unapproved_sample') {
      return alert.days_pending >= 7 ? 'high' : 'medium';
    }
    
    return 'medium';
  };

  const getAlertColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#ef4444';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getAlertSummary = (alert) => {
    switch (alert.alert_type) {
      case 'low_inventory':
        return `${alert.sku_name} - Stock: ${alert.current_stock}/${alert.threshold}`;
      case 'new_sample_request':
        return `${alert.creator_name} requested ${alert.sample_quantity} samples`;
      case 'stable_unapproved_sample':
        return `${alert.creator_name} - Pending for ${alert.days_pending} days`;
      default:
        return alert.message || 'Alert notification';
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    if (filter === 'inventory') return alert.source === 'inventory';
    if (filter === 'samples') return alert.source === 'samples';
    return true;
  });

  const stats = {
    total: alerts.length,
    inventory: alerts.filter(a => a.source === 'inventory').length,
    samples: alerts.filter(a => a.source === 'samples').length,
    highPriority: alerts.filter(a => getAlertPriority(a) === 'high').length
  };

  return (
    <div className="page-relative-wrapper">
      {loading && (
        <div className="page-overlay-loader">
          <Loader fullPage={false} text="Syncing Alerts..." />
        </div>
      )}
      <div className={`alerts-container ${loading ? 'content-blur' : ''}`}>
      {/* Header */}
      <div className="alerts-header">
        <div className="header-left">
          <GradientHeading>
            
            Alerts & Notifications
          </GradientHeading>
          <p className="alerts-subtitle">
            Monitor inventory alerts and sample request notifications
          </p>
        </div>
        <div className="header-actions">
          {/* --- Slack Integration Box --- */}
          <div className="webhook-container enhanced">
            <div className="webhook-header">
              <div className="webhook-icon-bg">
                <Slack size={28} color="#4A154B" />
              </div>
              <div className="webhook-title-group">
                <h3 className="webhook-title">Slack Integration</h3>
                <p className="webhook-description">
                  Connect your Slack workspace to receive instant alert notifications.<br />
                  <span style={{ color: '#4A154B', fontWeight: 500 }}>Stay in sync with your team!</span>
                </p>
              </div>
            </div>
            <div className="webhook-actions">
              <button
                className="connect-slack-btn gradient-btn"
                onClick={handleConnectSlack}
                disabled={isAuthenticating}
                style={{ marginRight: 12 }}
              >
                <Slack size={16} style={{ marginRight: 6 }} />
                {isAuthenticating ? 'Connecting...' : 'Connect Slack'}
              </button>
              <button
                className="test-slack-btn gradient-btn"
                onClick={handleTestSlack}
                disabled={isAuthenticating}
                style={{ marginRight: 12 }}
              >
                <RefreshCw size={16} style={{ marginRight: 6 }} />
                {isAuthenticating ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                className="disconnect-slack-btn gradient-btn"
                onClick={handleDisconnectSlack}
                disabled={isAuthenticating}
              >
                <AlertTriangle size={16} style={{ marginRight: 6 }} />
                {isAuthenticating ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
            {slackMessage && (
              <div className={`webhook-message ${slackMessage.type}`} style={{ marginTop: 12 }}>
                {slackMessage.text}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon total">
            <Bell color="#111" />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{stats.total}</h3>
            <p className="stat-label">Total Alerts</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon inventory">
            <Package color="#111" />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{stats.inventory}</h3>
            <p className="stat-label">Inventory Alerts</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon samples">
            <MessageSquare color="#111" />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{stats.samples}</h3>
            <p className="stat-label">Sample Alerts</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon high-priority">
            <AlertTriangle color="#111" />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{stats.highPriority}</h3>
            <p className="stat-label">High Priority</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-left">
          <div className="filter-group">
            <label className="filter-label">
              <Filter className="filter-icon" />
              Alert Type:
            </label>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Alerts</option>
              <option value="inventory">Inventory Only</option>
              <option value="samples">Samples Only</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">
              <Calendar className="filter-icon" />
              Time Range:
            </label>
            <select 
              value={days} 
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="filter-select"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Limit:</label>
            <select 
              value={limit} 
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="filter-select"
            >
              <option value={25}>25 alerts</option>
              <option value={50}>50 alerts</option>
              <option value={100}>100 alerts</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <AlertTriangle className="error-icon" />
          {error}
        </div>
      )}


      {/* Alerts List */}
      {!loading && !error && (
        <div className="alerts-list">
          {filteredAlerts.length === 0 ? (
            <div className="empty-state">
              <Bell className="empty-icon" />
              <h3>No alerts found</h3>
              <p>No alerts match your current filters. Try adjusting the time range or alert type.</p>
            </div>
          ) : (
            filteredAlerts.map((alert, index) => {
              const priority = getAlertPriority(alert);
              const priorityColor = getAlertColor(priority);
              
              return (
                <div 
                  key={`${alert.id || index}-${alert.sent_at}`} 
                  className="alert-card"
                  style={{ borderLeftColor: priorityColor }}
                >
                  <div className="alert-header">
                    <div className="alert-type">
                      {getAlertIcon(alert.alert_type)}
                      <span className="alert-type-label">
                        {getAlertTypeLabel(alert.alert_type)}
                      </span>
                      <span 
                        className="priority-badge"
                        style={{ backgroundColor: priorityColor }}
                      >
                        {priority.toUpperCase()}
                      </span>
                    </div>
                    <div className="alert-meta">
                      <span className="alert-time">
                        {formatDate(alert.sent_at)}
                      </span>
                      <span className="alert-source">
                        {alert.source === 'inventory' ? 'Inventory' : 'Samples'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="alert-content">
                    <p className="alert-message">{alert.message}</p>
                    <div className="alert-summary">
                      {getAlertSummary(alert)}
                    </div>
                  </div>
                  
                  {alert.brand_id && (
                    <div className="alert-footer">
                      <span className="brand-info">
                        Brand ID: {alert.brand_id}
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
      {/* Slack Connection Blur Overlay */}
      {isSlackConnecting && (
        <div 
          className="modal-overlay" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            cursor: 'pointer'
          }}
          onClick={() => setIsSlackConnecting(false)}
        >
          <div 
            className="modal-content" 
            style={{ 
              textAlign: 'center', 
              background: 'rgba(255, 255, 255, 0.05)', 
              backdropFilter: 'blur(20px)',
              padding: '40px',
              borderRadius: '28px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
            }}
          >
            <div className="section-header" style={{ justifyContent: 'center', border: 'none', marginBottom: 0 }}>
              <div className="icon-box" style={{ 
                width: '56px',
                height: '56px',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(74, 21, 75, 0.1)' 
              }}>
                <Slack size={32} color="#4A154B" />
              </div>
            </div>
            <h3 style={{ margin: '20px 0 10px', color: '#fff', fontSize: '1.75rem', fontWeight: 700 }}>Connecting to Slack...</h3>
            <p style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>Please complete the authorization in the popup window.</p>
            <button 
              className="btn-cancel" 
              style={{ 
                marginTop: '24px', 
                width: '100%',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #27272a',
                background: '#18181b',
                color: '#fff',
                cursor: 'pointer'
              }}
              onClick={() => setIsSlackConnecting(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
);
};

export default Alerts;
